/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Radio,
  RadioGroup,
} from '@heroui/react';
import { FaUserTie } from 'react-icons/fa6';
import { MdDangerous, MdWarning } from 'react-icons/md';
import { getSiblingEffectiveState } from '../../../../utils/quoteFlowRequests';
import CustomButton from '../../CustomButton';
import { Job } from '../../../../utils/type';
import { useTranslation } from 'react-i18next';

export interface CancelReasonOption {
  value: string;
  label: string;
}

export interface BroadcastProviderSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** All jobs in the broadcast batch */
  jobs: Job[];
  /**
   * Selected provider job IDs. The sentinel `'ALL'` may be present to
   * indicate "cancel all open requests". When `'ALL'` is present, it
   * should be the only entry.
   */
  selectedProviders: string[];
  onSelectedProvidersChange: (value: string[]) => void;
  /** 'provider' = pick providers, 'reason' = pick cancellation reason */
  step: 'provider' | 'reason';
  onStepChange: (step: 'provider' | 'reason') => void;
  /** Cancellation reasons list */
  cancelReasons: CancelReasonOption[];
  /** Currently selected cancellation reason value */
  selectedReason: string;
  onSelectedReasonChange: (value: string) => void;
  /** Called when user confirms the cancellation in the reason step */
  onConfirmCancel: () => void | Promise<void>;
  isCancelling?: boolean;
}

const ALL_SENTINEL = 'ALL';

const BroadcastProviderSelectModal = ({
  isOpen,
  onClose,
  jobs,
  selectedProviders,
  onSelectedProvidersChange,
  step,
  onStepChange,
  cancelReasons,
  selectedReason,
  onSelectedReasonChange,
  onConfirmCancel,
  isCancelling = false,
}: BroadcastProviderSelectModalProps) => {
  const { t } = useTranslation();

  const openJobs = jobs.filter(
    (j) => getSiblingEffectiveState(j) === 'requested'
  );

  const isAllSelected = selectedProviders.includes(ALL_SENTINEL);

  const toggleProvider = (jobId: string) => {
    // Clicking "ALL" clears individual selections and selects only ALL.
    if (jobId === ALL_SENTINEL) {
      onSelectedProvidersChange(isAllSelected ? [] : [ALL_SENTINEL]);
      return;
    }

    // If ALL was selected, clicking an individual provider should
    // deselect ALL and select only that provider.
    if (isAllSelected) {
      onSelectedProvidersChange([jobId]);
      return;
    }

    // Standard toggle
    const next = selectedProviders.includes(jobId)
      ? selectedProviders.filter((id) => id !== jobId)
      : [...selectedProviders, jobId];
    onSelectedProvidersChange(next);
  };

  const canProceed = selectedProviders.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      hideCloseButton
      size="2xl"
      // Let the ModalBody scroll instead of clipping content
      scrollBehavior="inside"
    >
      <ModalContent className="max-h-[90vh]">
        {step === 'provider' ? (
          <>
            {/* Fixed header area */}
            <div className="flex flex-col items-center space-y-3 pt-6 px-6 pb-3 shrink-0">
              <div className="p-2 rounded-full bg-danger/20 flex items-center justify-center">
                <MdDangerous className="text-danger" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 text-center">
                {t('jobs.cancel_quote_request_title', 'Cancel Quote Request')}
              </h3>
              <p className="text-sm text-gray-600 text-center">
                {t(
                  'jobs.cancel_quote_request_desc',
                  'Select which provider request you want to cancel, or choose to cancel all open requests in this batch.'
                )}
              </p>

              {/* Warning Message */}
              <div className="w-full p-4 rounded-xl bg-amber-50 border border-amber-200 flex gap-3 mt-2">
                <div className="text-amber-600 shrink-0 pt-0.5">
                  <MdWarning size={20} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    {t(
                      'jobs.cancel_cannot_undo_title',
                      'Cancellation Cannot Be Undone'
                    )}
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    {t(
                      'jobs.cancel_cannot_undo_desc',
                      "Once you cancel a request, the provider will be notified and you cannot reactivate it. You'll need to create a new request if you change your mind."
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Scrollable provider list */}
            <ModalBody className="overflow-y-auto px-6 py-2">
              <div className="space-y-3">
                {/* Cancel All Option Card */}
                <div
                  onClick={() => toggleProvider(ALL_SENTINEL)}
                  className={`p-2 rounded-xl border-1 cursor-pointer transition-all ${
                    isAllSelected
                      ? 'border-danger bg-danger/5'
                      : 'border-slate-200 bg-white hover:border-danger/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Checkbox (multi-select) */}
                    <div
                      className={`w-4 h-4 rounded-md border-1 flex items-center justify-center shrink-0 ${
                        isAllSelected
                          ? 'border-danger bg-danger'
                          : 'border-slate-300'
                      }`}
                    >
                      {isAllSelected && (
                        <svg
                          viewBox="0 0 12 12"
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M2 6l3 3 5-6" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-900">
                        {t(
                          'jobs.cancel_all_open_requests',
                          'Cancel All Open Requests'
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {t(
                          'jobs.cancel_all_open_requests_desc',
                          'Entire Broadcast Batch — All remaining providers will be notified'
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Individual Provider Cards */}
                {openJobs.map((providerJob) => {
                  const providerName =
                    providerJob.businessName ||
                    t('jobs.unknown_provider', 'Unknown Provider');
                  const serviceTitle =
                    providerJob.serviceTitle ||
                    providerJob.serviceName ||
                    t('jobs.service', 'Service');
                  const providerAvatar =
                    'avatar' in providerJob
                      ? (providerJob as { avatar?: string }).avatar
                      : undefined;

                  const isSelected =
                    !isAllSelected &&
                    selectedProviders.includes(providerJob.jobId);

                  return (
                    <div
                      key={providerJob.jobId}
                      onClick={() => toggleProvider(providerJob.jobId)}
                      className={`p-2 rounded-xl border-1 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-danger bg-danger/5'
                          : 'border-slate-200 bg-white hover:border-danger/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-4 h-4 rounded-md border-2 flex items-center justify-center shrink-0 ${
                            isSelected
                              ? 'border-danger bg-danger'
                              : 'border-slate-300'
                          }`}
                        >
                          {isSelected && (
                            <svg
                              viewBox="0 0 12 12"
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M2 6l3 3 5-6" />
                            </svg>
                          )}
                        </div>

                        {providerAvatar ? (
                          <img
                            src={providerAvatar}
                            alt={providerName}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center">
                            <FaUserTie className="text-slate-500" size={16} />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-slate-900 truncate">
                            {providerName}
                          </p>
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {serviceTitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ModalBody>

            <ModalFooter className="flex justify-center items-center gap-3 p-4 shrink-0 border-t border-slate-100">
              <CustomButton
                label={t('jobs.cancel', 'Cancel')}
                color="default"
                variant="flat"
                className="w-full"
                size="sm"
                onPress={onClose}
              />

              <CustomButton
                label={t('jobs.next', 'Next')}
                color="danger"
                className="w-full"
                size="sm"
                isDisabled={!canProceed}
                onPress={() => onStepChange('reason')}
              />
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalBody className="overflow-y-auto px-6 py-4">
              <div className="flex flex-col space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('jobs.cancellation_reason', 'Cancellation Reason')}
                  <span className="text-danger ml-1">*</span>
                </h3>
                <p className="text-gray-600 text-sm">
                  {t(
                    'jobs.cancellation_reason_prompt',
                    'Please select a reason for cancelling.'
                  )}
                </p>

                <div className="flex justify-start items-center">
                  <RadioGroup
                    size="sm"
                    value={selectedReason}
                    onValueChange={onSelectedReasonChange}
                  >
                    {cancelReasons.map((option) => (
                      <Radio
                        key={option.value}
                        value={option.value}
                        className="text-sm"
                      >
                        {option.label}
                      </Radio>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </ModalBody>

            <ModalFooter className="flex justify-center items-center gap-3 p-4 shrink-0 border-t border-slate-100">
              <CustomButton
                label={t('jobs.back', 'Back')}
                color="default"
                variant="flat"
                className="w-full"
                size="sm"
                onPress={() => onStepChange('provider')}
              />

              <CustomButton
                label={t('jobs.cancel_request', 'Cancel Request')}
                color="danger"
                className="w-full"
                size="sm"
                onPress={onConfirmCancel}
                isLoading={isCancelling}
                isDisabled={isCancelling}
              />
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default BroadcastProviderSelectModal;
